import { useCallback, useMemo, useState } from "react";
import type { SyntheticEvent } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation, useQuery } from "@tanstack/react-query";
import { parse, set } from "date-fns";
import { PlusIcon, Trash2Icon } from "lucide-react";
import uuid from "uuid";

import { formatDate, toClearableApiDateTime } from "simplydo/core/dates";
import { asMutation, initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { DateTimeInput } from "simplydo/react/ui/date";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { Icon } from "simplydo/react/ui/icon";
import { type InputOnChangeData } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Placeholder, PlaceholderLine } from "simplydo/react/ui/placeholder";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import CalendarEventEditModal from "components/calendars/Calendar/CalendarEventEditModal";
import {
  CalendarEditingEvent,
  CalendarResponseEvent,
  CalendarType,
  type CellAction,
  ChallengeCalendarEvent,
  CreateCalendarEventBody,
} from "components/calendars/Calendar/calendar-types";
import AnnouncementModal from "components/lib/AnnouncementManager/AnnouncementModal";
import EmailPreviewModal from "components/lib/AnnouncementManager/EmailPreviewModal";
import { challengeStandardConditions } from "components/lib/AnnouncementManager/conditions";
import type {
  AnnouncementSetState,
  EditableAnnouncement,
} from "components/lib/AnnouncementManager/types";
import {
  EMPTY_ANNOUNCEMENT,
  isAnnouncementClosed,
} from "components/lib/AnnouncementManager/types";
import TimezoneExplainer from "components/lib/TimezoneExplainer";

import FullCalendarView from "../../../calendars/Calendar/FullCalendarView";
import type { ChallengeDetail } from "../../types";
import GeneratePlanModal from "./GeneratePlanModal";

const SubmissionInputDiv = styled.div`
  display: ${({ theme }) => (theme.sizes.isMobile ? "block" : "flex")};
  flex-direction: row;
  align-items: center;
`;

type PlannerProps = { challenge: ChallengeDetail };

const toEventDate = (value?: string | Date | null): Date =>
  !value ? new Date() : value instanceof Date ? value : new Date(value ?? "");

const toIsoDate = (value?: string | Date | null): string | undefined => {
  if (!value) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value ?? "");
  return date.toISOString();
};

const Planner = ({ challenge }: PlannerProps) => {
  const [editingEvent, setEditingEvent] = useState<CalendarEditingEvent | null>(
    null,
  );
  const [eventFilter, setEventFilter] = useState("all");
  const [generatePlanModalOpen, setGeneratePlanModalOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);
  const [notifyModalOpen, setNotifyModalOpen] = useState(false);
  const [notifyMemberCount, setNotifyMemberCount] = useState(0);

  const { t } = useTranslation();

  const user = useAppSelector((state) => state.user);
  const canPublishChallenges = util.hasPermission(
    user,
    "org.publishChallenges",
    user?.ownerOrganisation?._id,
  );
  const canManageChallenge = util.canManageChallenge(user, challenge);

  const challengeId = challenge._id;
  const calendarId = challenge.calendar?._id;

  const calendarQuery = useQuery(
    initUseQuery(api.calendars.getCalendar, [calendarId!], {
      isEnabled: !!calendarId,
    }),
  );
  const calendar = calendarQuery.data?.calendar ?? null;

  const setEventContent: AnnouncementSetState = (contentOrCallback) => {
    setEditingEvent((prev) => {
      if (!prev) return prev;
      const prevContent = {
        ...EMPTY_ANNOUNCEMENT,
        ...(prev.eventContent as EditableAnnouncement | undefined),
      };
      const nextContent =
        typeof contentOrCallback === "function"
          ? contentOrCallback(prevContent)
          : contentOrCallback;
      if (isAnnouncementClosed(nextContent)) {
        return null;
      }
      return {
        ...prev,
        eventContent: nextContent as NonNullable<
          CalendarEditingEvent["eventContent"]
        >,
      };
    });
  };

  const patchEditingEvent = (
    patch:
      | Partial<CalendarEditingEvent>
      | ((prev: CalendarEditingEvent) => Partial<CalendarEditingEvent>),
  ) => {
    setEditingEvent((prev) => {
      if (!prev) {
        return prev;
      }
      const nextPatch = typeof patch === "function" ? patch(prev) : patch;
      return { ...prev, ...nextPatch };
    });
  };

  const copyToNew = (announcement: EditableAnnouncement) => {
    const now = new Date();

    const { _id, ...copied } = announcement;
    if (announcement.sendAt) {
      const sendAt = new Date(announcement.sendAt);
      copied.sendAt = sendAt > now ? sendAt.toISOString() : undefined;
    }

    setEditingEvent({
      type: "announcement",
      from: copied.sendAt ? new Date(copied.sendAt) : now,
      eventContent: copied as NonNullable<CalendarEditingEvent["eventContent"]>,
    });
    toast.success("Announcement copied");
  };

  const createEvent = (
    event: CalendarEditingEvent,
    shouldNotifyMembers = true,
  ) => {
    if (!calendarId || !event.from) {
      return;
    }
    const body: CreateCalendarEventBody = {
      id: uuid.v4(),
      type: event.type || "event",
      notifyMembers: shouldNotifyMembers,
      title: event.title ?? "",
      description: event.description,
      from: toIsoDate(event.from) ?? new Date().toISOString(),
      to: toIsoDate(event.to),
      allDay: event.allDay,
      color: event.color,
      members: event.members,
      organiserName: event.organiserName ?? "",
      organiserEmail: event.organiserEmail ?? "",
      notifications: event.notifications,
    };
    api.calendars.createCalendarEvent(
      calendarId,
      body as OpenAPI.POST<"/calendars/{calendar_id}/calendar/events">["requestBody"],
      ({ events }) => {
        setEditingEvent(null);
        api.setQueryData(api.calendars.getCalendar, [calendarId], (prev) => ({
          ...prev,
          calendar: { ...prev?.calendar, events },
        }));
      },
      (error) => {
        toast.error(error.message);
      },
    );
  };

  const saveCalendarEvent = (
    event: CalendarEditingEvent & { id?: string },
    shouldNotifyMembers = true,
  ) => {
    if (!calendarId || !event.id) {
      return;
    }
    const calendarEventType =
      event.type === "meeting" || event.type === "reminder"
        ? "event"
        : event.type;

    const payload: CalendarResponseEvent & { notifyMembers?: boolean } = {
      id: event.id,
      type: calendarEventType,
      title: event.title,
      description: event.description,
      color: event.color,
      members: event.members,
      organiserName: event.organiserName,
      organiserEmail: event.organiserEmail,
      notifications: event.notifications,
      notifyMembers: shouldNotifyMembers,
      allDay: event.allDay,
      from: toIsoDate(event.from),
      to: event.type !== "event" ? undefined : toIsoDate(event.to),
    };

    api.calendars.updateCalendarEvent(
      calendarId,
      event.id,
      payload as OpenAPI.POST<"/calendars/{calendar_id}/calendar/events/{event_id}">["requestBody"],
      ({ events }) => {
        setEditingEvent(null);
        api.setQueryData(api.calendars.getCalendar, [calendarId], (prev) => ({
          ...prev,
          calendar: { ...prev?.calendar, events },
        }));
      },
      (error) => {
        toast.error(error.message);
      },
    );
  };

  const handleClickCreateEvent = () => {
    if (!editingEvent) {
      return;
    }
    const memberCount = (editingEvent.members || []).length;
    if (memberCount <= 1) {
      submitEditingEvent(false);
      return;
    }
    setNotifyMemberCount(memberCount);
    setNotifyModalOpen(true);
  };

  const submitEditingEvent = (shouldNotifyMembers: boolean) => {
    if (!editingEvent) {
      return;
    }
    if (editingEvent.id) {
      saveCalendarEvent(editingEvent, shouldNotifyMembers);
    } else {
      createEvent(editingEvent, shouldNotifyMembers);
    }
  };

  const deleteAnnouncement = (announcementId: string) => {
    if (!challengeId || !calendarId) {
      return;
    }
    api.announcements.delete(
      "challenge",
      challengeId,
      announcementId,
      () => {
        api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  };

  const updateOpenWithComms = (openWithComms: boolean) => {
    if (!challengeId) {
      return;
    }
    api.challenges.update(
      challengeId,
      { openWithComms },
      () => {
        api.setQueryData(
          api.challenges.get,
          [challengeId],
          (prevChallenge) => ({ ...(prevChallenge || {}), openWithComms }),
        );
      },
      () => {
        toast.error("Failed to save default preferences");
      },
    );
  };

  const updateChallenge = (
    update: Partial<OpenAPI.PUT<"/challenges/{id}">["requestBody"]>,
  ) => {
    if (!challengeId || !calendarId) {
      return;
    }
    api.challenges.update(
      challengeId,
      update,
      (data) => {
        api.setQueryData(api.challenges.get, [challengeId], data);
        api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
      },
      () => {
        toast.error("Failed to save default preferences");
      },
    );
  };

  const setNewOpenDate = (enable: boolean, openDate: string | Date | null) => {
    updateChallenge({ openDate: toClearableApiDateTime(enable, openDate) });
  };

  const setNewDeadline = (enable: boolean, deadline: string | Date | null) => {
    updateChallenge({ deadline: toClearableApiDateTime(enable, deadline) });
  };

  const saveTemplate = (
    update: Partial<OpenAPI.PUT<"/challenges/{id}/template">["requestBody"]>,
  ) => {
    if (!challenge._id || !calendarId) {
      return;
    }
    api.challenges.updateTemplate(
      challenge._id,
      update,
      () => {
        toast.success("Assessment deadline updated");
        api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  };

  const setNewAssessmentDeadline = (
    enable: boolean,
    deadline: string | Date | null,
  ) => {
    saveTemplate({
      assessmentDeadline: toClearableApiDateTime(enable, deadline),
    });
  };

  const deleteEvent = (eventId: string, eventType: string) => {
    util
      .confirm("Delete event", "Are you sure you want to delete this event?")
      .then(() => {
        if (eventType === "announcement") {
          deleteAnnouncement(eventId);
        } else if (eventType === "open") {
          setNewOpenDate(false, null);
        } else if (eventType === "deadline") {
          setNewDeadline(false, null);
        } else if (eventType === "assessmentDeadline") {
          setNewAssessmentDeadline(false, null);
        } else {
          if (!calendarId) {
            return;
          }
          api.calendars.deleteCalendarEvent(
            calendarId,
            eventId,
            ({ events }) => {
              api.setQueryData(
                api.calendars.getCalendar,
                [calendarId],
                (prev) => ({
                  ...prev,
                  calendar: { ...prev?.calendar, events },
                }),
              );
            },
            (error) => {
              toast.error(error.message);
            },
          );
        }
      })
      .catch(() => {});
  };

  const cellActions = useMemo(() => {
    const actions: CellAction[] = [
      {
        name: "Schedule an announcement",
        action: (dayDate) => ({
          type: "announcement",
          from: dayDate,
          eventContent: { sendAt: dayDate },
        }),
      },
    ];

    if (challenge?.stage !== "published") {
      actions.push({
        name: "Set open date",
        action: (dayDate) => ({ type: "open", from: dayDate }),
      });
    }

    actions.push({
      name: "Set deadline",
      action: (dayDate) => ({ type: "deadline", from: dayDate }),
    });

    return actions;
  }, [challenge]);

  const handleCloseModal = () => {
    setEditingEvent(null);
  };

  const filteredEvents = (calendar?.events || []).filter(
    (event: ChallengeCalendarEvent) => {
      // Do not filter out background events for now as they should probably always display
      if (event.eventType === "background") {
        return true;
      }
      if (eventFilter === "member") {
        if (!event.members) {
          return false;
        }
        return user?._id ? event.members.includes(user._id) : false;
      }
      if (eventFilter !== "all" && event.plan !== eventFilter) {
        return false;
      }
      return true;
    },
  );

  const deletePlanMutation = useMutation({
    mutationKey: ["calendars", calendarId, "plans"],
    mutationFn: asMutation(api.calendars.deletePlan),
    onSuccess: () => {
      toast.success("Plan deleted successfully");
      setEventFilter("all");
      if (calendarId) {
        api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
      }
    },
  });

  const deletePlan = useCallback(
    (plan: NonNullable<CalendarType["plans"]>[number]) => {
      if (!calendarId || !plan.id) {
        return;
      }
      util
        .confirm(
          `Delete ${plan.name ?? "plan"}`,
          "Are you sure you want to delete this plan? This will remove all events associated with this plan from the calendar.",
        )
        .then(() => {
          deletePlanMutation.mutate([calendarId, plan.id!]);
        });
    },
    [deletePlanMutation, calendarId],
  );

  const editingAnnouncement: EditableAnnouncement | null =
    editingEvent?.type === "announcement"
      ? {
          ...EMPTY_ANNOUNCEMENT,
          ...(editingEvent.eventContent as EditableAnnouncement | undefined),
        }
      : null;

  const handleCalendarDragUpdate = (event: CalendarResponseEvent) => {
    saveCalendarEvent({
      ...event,
      from: toEventDate(event.from),
      to: event.to ? toEventDate(event.to) : undefined,
    });
  };

  return (
    <div>
      {calendar && calendarId && challengeId && generatePlanModalOpen && (
        <GeneratePlanModal
          generatePlanModalOpen={generatePlanModalOpen}
          onClose={() => setGeneratePlanModalOpen(false)}
          challengeId={challengeId}
          calendarId={calendarId}
          calendar={calendar}
        />
      )}
      <CalendarEventEditModal
        event={editingEvent}
        forType="challenge"
        forId={challengeId}
        showMembers
        onPatch={patchEditingEvent}
        onCancel={handleCloseModal}
        onSave={handleClickCreateEvent}
      />
      <Modal open={notifyModalOpen}>
        <ModalHeader>Notify users</ModalHeader>
        <ModalContent>
          {`This event has ${util.pluralise(notifyMemberCount, "member", "members")}, would you like to notify them about this event?`}
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setNotifyModalOpen(false)}>Cancel</Button>
          <Button
            onClick={() => {
              setNotifyModalOpen(false);
              submitEditingEvent(false);
            }}
          >
            Just save
          </Button>
          <Button
            onClick={() => {
              setNotifyModalOpen(false);
              submitEditingEvent(true);
            }}
            color="primary"
          >
            Save and notify
          </Button>
        </ModalActions>
      </Modal>
      <Modal open={editingEvent?.type === "open"}>
        <ModalHeader>
          {t("common:capitalise", { key: "generic.challenge" })} open date
        </ModalHeader>
        <ModalContent>
          <p>
            Set an open date for your {t("generic.challenge")}. If an open date
            is set, the {t("generic.challenge")} will automatically open at this
            point.
          </p>
          <TimezoneExplainer style={{ marginBottom: 10 }} />
          <SubmissionInputDiv>
            <Checkbox
              checked={!!challenge?.openDate}
              onChange={() =>
                setNewOpenDate(!challenge?.openDate, editingEvent?.from ?? null)
              }
              disabled={!canPublishChallenges}
              data-tooltip={
                !canPublishChallenges
                  ? `Only users with the "Publish ${t("generic.challenges")}" permission can open ${t("generic.challenges")}.`
                  : null
              }
            >
              {`There is an open date for this ${t("generic.challenge")} of`}
            </Checkbox>
            {canPublishChallenges ? (
              <DateTimeInput
                disableMinute={true}
                value={toEventDate(editingEvent?.from)}
                dateTimeFormat="yyyy-MM-dd HH:mm"
                iconPosition="left"
                onClear={() => patchEditingEvent({ from: new Date() })}
                style={{ marginLeft: 10, marginRight: 10 }}
                onChange={(
                  _e: SyntheticEvent,
                  { value }: InputOnChangeData,
                ) => {
                  const nextDate = parse(value, "yyyy-MM-dd HH:mm", new Date());
                  patchEditingEvent({ from: nextDate });
                  if (challenge?.openDate) {
                    setNewOpenDate(!!challenge.openDate, nextDate);
                  }
                }}
              />
            ) : (
              <Label style={{ marginLeft: 7 }}>
                {formatDate(challenge?.openDate, "yyyy-MM-dd HH:mm")}
              </Label>
            )}
          </SubmissionInputDiv>
          <Checkbox
            style={{ marginTop: 10 }}
            checked={!!challenge?.openWithComms}
            onChange={() => updateOpenWithComms(!challenge?.openWithComms)}
            disabled={!canPublishChallenges}
          >
            {`Send notifications and/or emails to users with interests that match this ${t("generic.challenge")} when the ${t("generic.challenge")} is opened.`}
          </Checkbox>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <Button
            onClick={() => {
              setNewOpenDate(!!challenge?.openDate, editingEvent?.from ?? null);
              handleCloseModal();
            }}
            color="primary"
          >
            Save
          </Button>
        </ModalActions>
      </Modal>
      <Modal open={editingEvent?.type === "deadline"}>
        <ModalHeader>
          {t("common:capitalise", { key: "generic.challenge" })} deadline
        </ModalHeader>
        <ModalContent>
          <p>
            Set a deadline for your {t("generic.challenge")}. If a deadline is
            set, the {t("generic.challenge")} will automatically close at this
            point.
          </p>
          <TimezoneExplainer style={{ marginBottom: 10 }} />
          <SubmissionInputDiv>
            <Checkbox
              checked={!!challenge?.deadline}
              onChange={() =>
                setNewDeadline(!challenge?.deadline, editingEvent?.from ?? null)
              }
            >
              {`There is a deadline for this ${t("generic.challenge")} of`}
            </Checkbox>
            <DateTimeInput
              disableMinute={true}
              value={toEventDate(editingEvent?.from)}
              minDate={set(new Date(), {
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0,
              })}
              dateTimeFormat="yyyy-MM-dd HH:mm"
              iconPosition="left"
              onClear={() => patchEditingEvent({ from: new Date() })}
              style={{ marginLeft: 10, marginRight: 10 }}
              onChange={(_e: SyntheticEvent, { value }: InputOnChangeData) => {
                const nextDate = parse(value, "yyyy-MM-dd HH:mm", new Date());
                patchEditingEvent({ from: nextDate });
                setNewDeadline(true, nextDate);
              }}
            />
          </SubmissionInputDiv>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <Button
            onClick={() => {
              setNewDeadline(!!challenge?.deadline, editingEvent?.from ?? null);
              handleCloseModal();
            }}
            color="primary"
          >
            Save
          </Button>
        </ModalActions>
      </Modal>
      <Modal open={editingEvent?.type === "assessmentDeadline"}>
        <ModalHeader>Assessment deadline</ModalHeader>
        <ModalContent>
          <p>Set a deadline for assessment</p>
          <TimezoneExplainer style={{ marginBottom: 10 }} />
          <SubmissionInputDiv>
            <p style={{ margin: 0 }}>
              There is a deadline for this assessment of
            </p>
            <DateTimeInput
              disableMinute={true}
              value={toEventDate(editingEvent?.from)}
              minDate={set(new Date(), {
                hours: 0,
                minutes: 0,
                seconds: 0,
                milliseconds: 0,
              })}
              dateTimeFormat="yyyy-MM-dd HH:mm"
              iconPosition="left"
              onClear={() => patchEditingEvent({ from: new Date() })}
              style={{ marginLeft: 10, marginRight: 10 }}
              onChange={(_e: SyntheticEvent, { value }: InputOnChangeData) => {
                const nextDate = parse(value, "yyyy-MM-dd HH:mm", new Date());
                patchEditingEvent({ from: nextDate });
                setNewAssessmentDeadline(true, nextDate);
              }}
            />
          </SubmissionInputDiv>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => handleCloseModal()}>Cancel</Button>
          <Button
            onClick={() => {
              setNewAssessmentDeadline(
                !!challenge?.ownerIdeaTemplate?.assessmentDeadline,
                editingEvent?.from ?? null,
              );
              handleCloseModal();
            }}
            color="primary"
          >
            Save
          </Button>
        </ModalActions>
      </Modal>
      {canManageChallenge ||
      util.hasPermission(user, "challenge.editSettings", challengeId) ? (
        <EmailPreviewModal
          isOpen={emailPreviewOpen}
          onClose={() => setEmailPreviewOpen(false)}
          forType={"challenge"}
          forId={challengeId}
          announcement={
            editingEvent?.type === "announcement"
              ? editingEvent.eventContent
              : undefined
          }
        />
      ) : null}
      {(canManageChallenge ||
        util.hasPermission(user, "challenge.editSettings", challengeId)) &&
      editingAnnouncement !== null ? (
        <AnnouncementModal
          enabledAnnouncementTypes={["standard"]}
          forType={"challenge"}
          forId={challengeId}
          standardConditions={challengeStandardConditions}
          standardConditionsRequired
          automaticAnnouncements={[]}
          setAutomaticAnnouncements={() => {}}
          scheduledAnnouncements={[]}
          setScheduledAnnouncements={() => {
            if (calendarId) {
              api.invalidateQueries(api.calendars.getCalendar, [calendarId]);
            }
          }}
          copyToNew={copyToNew}
          announcement={editingAnnouncement as EditableAnnouncement}
          setAnnouncement={setEventContent}
          deleteAnnouncement={deleteAnnouncement}
          setEmailPreviewOpen={setEmailPreviewOpen}
        />
      ) : null}
      <Card fluid>
        <CardContent>
          {calendarQuery.isLoading ? (
            <Placeholder>
              <PlaceholderLine style={{ height: 50 }} />
              <PlaceholderLine style={{ height: 400 }} />
            </Placeholder>
          ) : (
            <>
              {user?.ownerOrganisation?.enabledFeatures?.includes(
                "aiPlanner",
              ) ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: 10,
                  }}
                >
                  <Button
                    onClick={() => setGeneratePlanModalOpen(true)}
                    color="primary"
                  >
                    <Icon icon={PlusIcon} />
                    Generate engagement plan
                  </Button>
                </div>
              ) : null}

              <FullCalendarView
                toolbarExtra={
                  <Dropdown
                    variant="outline"
                    options={[
                      { key: "all", text: "All events", value: "all" },
                      {
                        key: "member",
                        text: "Only events I'm a member of",
                        value: "member",
                      },
                      ...(calendar?.plans || []).map((plan) => ({
                        key: plan.id,
                        text: plan.name,
                        value: plan.id,
                        content: (
                          <div
                            style={{
                              display: "flex",
                              lineHeight: "18px",
                              justifyContent: "space-between",
                              wordBreak: "break-word",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              {(plan.name?.length ?? 0) > 30 ? (
                                <>{plan.name?.slice(0, 30)}...</>
                              ) : (
                                plan.name
                              )}
                            </span>
                            <Button
                              size="small"
                              style={{ marginLeft: 5 }}
                              onClick={() => deletePlan(plan)}
                            >
                              <Icon icon={Trash2Icon} />
                            </Button>
                          </div>
                        ),
                      })),
                    ]}
                    value={eventFilter}
                    onChange={(_, { value }) => setEventFilter(value as string)}
                    aria-label="Event filter"
                  />
                }
                events={filteredEvents}
                updateEvent={handleCalendarDragUpdate}
                deleteEvent={
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editSettings",
                    challengeId,
                  )
                    ? deleteEvent
                    : undefined
                }
                leaveEvent={(eventId) => {
                  if (!calendarId) {
                    return;
                  }
                  util
                    .confirm(
                      "Leave event",
                      "Are you sure you want to leave this event?",
                    )
                    .then(() => {
                      api.calendars.leaveCalendarEvent(
                        calendarId,
                        eventId,
                        ({ events }) => {
                          api.setQueryData(
                            api.calendars.getCalendar,
                            [calendarId],
                            (prev) => ({
                              ...prev,
                              calendar: { ...prev.calendar, events },
                            }),
                          );
                        },
                        () => {},
                      );
                    })
                    .catch(() => {});
                }}
                setEditingEvent={setEditingEvent}
                editable={
                  canManageChallenge ||
                  util.hasPermission(
                    user,
                    "challenge.editSettings",
                    challengeId,
                  )
                }
                showMembers
                cellActions={cellActions}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Planner;
